import { Alert, Button, DeleteOutlinedIcon, FormItem, Modal, Select } from 'elmo-elements';
import { DateTime } from 'luxon';
import { DATE_FORMAT } from 'src/constants';
import React from 'react';
import { useAppState } from 'src/context';
import type { TPostBookingPayload } from 'src/types';
import { DatePicker } from 'src/components/DatePicker';
import debounce from 'lodash/debounce';
import { SelectLoadingOverlay } from 'src/components/SelectLoadingOverlay';
import { useModalState } from './hooks';
import { StyledControls } from './styles';

type TProps = {
    onDeleteClick: () => void;
    handleCallOnCreateConfirmationModal: (
        newConfirmationData: TPostBookingPayload & { message: string }
    ) => void;
};

export const MainModal = (props: TProps) => {
    const { onDeleteClick, handleCallOnCreateConfirmationModal } = props;
    const {
        subLocations,
        users,
        booking,
        isFetching,
        submit,
        errors,
        onChange,
        serverErrors,
        setServerErrors,
        timeZone,
        isOwnBooking,
        deleteBooking,
        handleCheckIsDateDisabled,
        changeTimezone,
        handleLoadMore,
        searchHandler,
        isUsersLoading,
    } = useModalState({ handleCallOnCreateConfirmationModal });

    const {
        actions: { handleCloseModal },
    } = useAppState();
    return (
        <Modal
            id="booking-modal"
            title={booking.id ? 'Edit Booking' : 'Add Booking'}
            type="medium"
            isOpened
            closeModal={() => handleCloseModal()}
            primaryButton={
                <Button
                    id="save-booking"
                    ariaLabel="save-booking"
                    type="primary"
                    isLoading={isFetching}
                    isDisabled={isFetching}
                    onClick={() => submit()}
                >
                    Save
                </Button>
            }
        >
            {!!serverErrors.length && (
                <Alert
                    id="booking-error"
                    role="alert"
                    type="danger"
                    onClose={() => {
                        setServerErrors([]);
                    }}
                >
                    {serverErrors.join(', ')}
                </Alert>
            )}
            {booking.id && (
                <StyledControls>
                    <Button
                        id="delete-booking"
                        ariaLabel="delete booking"
                        icon={<DeleteOutlinedIcon />}
                        onClick={() => (isOwnBooking ? deleteBooking() : onDeleteClick())}
                    />
                </StyledControls>
            )}
            <FormItem
                label="Employee"
                message={errors.user_id ? errors.user_id[0] : ''}
                status={errors.user_id && errors.user_id[0] ? 'error' : 'default'}
            >
                <SelectLoadingOverlay isLoading={isUsersLoading}>
                    <Select
                        id="users-select"
                        options={users}
                        onMenuScrollToBottom={debounce(handleLoadMore, 400)}
                        onChange={({ value }) => {
                            onChange(value, 'user_id');
                        }}
                        value={users.find((u: { value: string }) => u.value === booking.user_id)}
                        ariaLabel="Select user"
                        onInputChange={(value, { action }) => {
                            if (action === 'input-change' && value.length > 0) {
                                searchHandler(value);
                            }
                        }}
                    />
                </SelectLoadingOverlay>
            </FormItem>

            <FormItem
                label="Sub location"
                message={errors.sublocation_id ? errors.sublocation_id[0] : ''}
                status={errors.sublocation_id && errors.sublocation_id[0] ? 'error' : 'default'}
            >
                <Select
                    id="sublocations-select"
                    options={subLocations}
                    onChange={({ value }) => {
                        onChange(value, 'sublocation_id');
                    }}
                    value={subLocations.find(
                        (s: { value: string }) => s.value === booking.sublocation_id
                    )}
                    ariaLabel="Select sub location"
                />
            </FormItem>

            <FormItem
                label="Date"
                message={errors.booking_date ? errors.booking_date[0] : ''}
                status={errors.booking_date && errors.booking_date[0] ? 'error' : 'default'}
            >
                <DatePicker
                    id="datepicker"
                    firstDayOfWeek={1}
                    name="selectedDate"
                    value={DateTime.fromISO(booking.booking_date, { zone: timeZone }).toJSDate()}
                    onChange={(value) => {
                        if (value) {
                            onChange(
                                DateTime.fromJSDate(changeTimezone(value, timeZone)).toFormat(
                                    DATE_FORMAT
                                ),
                                'booking_date'
                            );
                        }
                    }}
                    ariaLabel="Selected date"
                    disabledDate={handleCheckIsDateDisabled}
                />
            </FormItem>
        </Modal>
    );
};
